import React from 'react';

const URL_REGEX = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

// Trim trailing punctuation that's almost certainly sentence/parenthetical
// punctuation rather than part of the URL. Closing brackets are only trimmed
// when there is no matching opener inside the URL, so wiki-style links like
// https://en.wikipedia.org/wiki/React_(software) survive intact.
function stripTrailing(url: string): { url: string; trailing: string } {
  let trailing = '';
  while (url.length > 0) {
    const last = url[url.length - 1];
    const plain = /[.,;:!?'"`]/.test(last);
    const unmatched =
      (last === ')' && !url.includes('(')) ||
      (last === ']' && !url.includes('[')) ||
      (last === '}' && !url.includes('{'));
    if (plain || unmatched) {
      trailing = last + trailing;
      url = url.slice(0, -1);
    } else {
      break;
    }
  }
  return { url, trailing };
}

interface Options {
  /** Stop link clicks from bubbling — use when a parent has its own onClick
   *  (inline-edit triggers, clickable cards) that shouldn't fire on link clicks. */
  stopPropagation?: boolean;
  /** Key prefix to disambiguate when callers concatenate multiple linkify outputs. */
  keyPrefix?: string;
}

export function linkify(text: string | undefined | null, opts: Options = {}): React.ReactNode[] {
  if (!text) return [];
  const parts: React.ReactNode[] = [];
  const prefix = opts.keyPrefix || 'u';
  let last = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(URL_REGEX.source, URL_REGEX.flags);
  while ((match = re.exec(text)) !== null) {
    const raw = match[0];
    const { url, trailing } = stripTrailing(raw);
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (url) {
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      parts.push(
        <a
          key={`${prefix}-${match.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="pm-autolink"
          onClick={opts.stopPropagation ? (e) => e.stopPropagation() : undefined}
        >
          {url}
        </a>,
      );
    }
    if (trailing) parts.push(trailing);
    last = match.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
